// Core packages

// Third party packages

// Custom packages
import { DataTypeSummary } from "src/app/shared/interfaces/data-type.interface";

/**
 * Pure helpers shared by the document-template page and its dialogs: client side
 * mirror of the back-end validation rules and labelling of the data types catalogue.
 *
 * Ordering lives entirely on the back-end: the `/reorder` endpoints take the new
 * sequence of ids and answer with the level already sorted, so the front-end computes
 * no `position` at all.
 *
 * @since 3.0.0
 */

// BEGIN - Validation patterns (mirror of the back-end validators)

/** Area code, e.g. 'A', 'R3' */
export const AREA_CODE_PATTERN = /^[A-Z][A-Z0-9]{0,4}$/;

/** Document key, e.g. 'SB1', 'PA30' */
export const DOC_KEY_PATTERN = /^[A-Za-z0-9\/_-]{1,20}$/;

/** Min/max length accepted by the back-end on both translations */
export const TRANSLATION_MIN_LENGTH = 1;
export const TRANSLATION_MAX_LENGTH = 300;

/** Max length accepted by the back-end on the description of a document type */
export const DESCRIPTION_MAX_LENGTH = 2000;

// END - Validation patterns


// BEGIN - Description of a document type

/**
 * Description as the editor shows it: the stored text, '' when there is none. Absent,
 * null and '' all mean "no description". Nothing is trimmed here: the text is shown
 * exactly as it was stored, line breaks included.
 *
 * @since 3.0.0
 */
export function readDescription(value: any): string {
  return typeof value === "string" ? value : "";
}

/**
 * Description as it is compared and sent: trimmed the way the back-end stores it, ''
 * when nothing is left. Anything that is not a string counts as no description.
 *
 * @since 3.0.0
 */
export function normalizeDescription(value: any): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Description to put in a POST body, undefined when the key has to stay out of it:
 * only a text with something left once trimmed is sent.
 *
 * @since 3.0.0
 *
 * @param value Current value of the control
 */
export function descriptionForCreate(value: any): string | undefined {
  const description = normalizeDescription(value);
  return description ? description : undefined;
}

/**
 * Description to put in a PUT body, undefined when it did not change and the key has
 * to stay out of it (the back-end then keeps the stored one).
 *
 * Emptying the field yields '', NEVER null nor undefined: ApiService drops those keys
 * from the body, and the back-end would then keep the previous description instead of
 * clearing it.
 *
 * @since 3.0.0
 *
 * @param value  Current value of the control
 * @param stored Description of the loaded document, possibly absent
 */
export function descriptionForUpdate(
  value: any,
  stored: any
): string | undefined {
  const description = normalizeDescription(value);
  return description !== normalizeDescription(stored) ? description : undefined;
}

// END - Description of a document type


// BEGIN - Duplicated labels

/**
 * A node carrying the localized labels the uniqueness rule works on. Structural on
 * purpose: areas and sections are checked by the very same code.
 *
 * @since 3.0.0
 */
export interface TranslatedNode {
  _id?: string;
  deleted?: boolean;
  translation?: { it?: string; en?: string };
}

/** Which label of a node collided, and whether the owner of that label is hidden */
export interface LabelConflict {
  /** The label as it is stored on the colliding node (NOT the normalized form) */
  label: string;
  /** True when the colliding node is soft deleted: the user cannot see it in the tree */
  deleted: boolean;
}

/**
 * Drop the accents of a string, e.g. "Attività" -> "Attivita".
 *
 * @since 3.0.0
 */
function stripAccents(value: string): string {
  // NFD splits an accented character into its base letter plus a combining mark, which
  // is then dropped. Safe here: the browsers this app targets all implement it.
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * Comparison form of a label: the client side mirror of the back-end uniqueness rule,
 * which ignores case, spaces, punctuation and accents. "Linea vita", "linea-vita" and
 * "LINEAVITA" all collapse onto "lineavita".
 *
 * Only the latin alphabet is kept, exactly like the labels this application deals with
 * (italian and english). The back-end stays the authority: whatever it answers is shown
 * as is, so a label this function cannot judge is simply not anticipated.
 *
 * @since 3.0.0
 */
export function normalizeLabel(value: string): string {
  const source = value === null || value === undefined ? "" : value.toString();
  if (!source) {
    return "";
  }
  return stripAccents(source)
    .replace(/[^A-Za-z0-9]/g, "")
    .toLowerCase();
}

/**
 * True when the label carries at least one letter or digit, i.e. when the back-end
 * accepts it as a source for the internal identifier ("***" is refused with a 400).
 *
 * Anything outside ASCII is taken for a letter instead of being judged: better to let
 * the back-end refuse a label than to block one it would have accepted.
 *
 * @since 3.0.0
 */
export function labelHasLetterOrDigit(value: string): boolean {
  const source = value === null || value === undefined ? "" : value.toString();
  if (!source) {
    return false;
  }
  return /[A-Za-z0-9]/.test(stripAccents(source)) || /[^\x00-\x7F]/.test(source);
}

/**
 * Look for a node already using the given label in the given language, so that the
 * conflict can be shown while the user types instead of after a rejected save.
 *
 * An ACTIVE owner wins over a hidden one: the two cases deserve different wordings
 * (a hidden row is invisible in the tree, so its name looks free) and the active one
 * is the case the user can act upon straight away.
 *
 * @since 3.0.0
 *
 * @param nodes       Siblings to compare against (areas, or the sections of one area)
 * @param language    Which of the two labels is being validated
 * @param value       Label typed by the user
 * @param [excludeId] Node being edited, which must not collide with itself
 */
export function findLabelConflict(
  nodes: TranslatedNode[],
  language: "it" | "en",
  value: string,
  excludeId?: string
): LabelConflict {
  const normalized = normalizeLabel(value);
  if (!normalized) {
    return null;
  }
  const list = Array.isArray(nodes) ? nodes : [];
  const skipped = excludeId ? excludeId.toString() : "";
  let hidden: LabelConflict = null;

  for (let index = 0; index < list.length; index++) {
    const node = list[index];
    if (!node || !node._id || node._id.toString() === skipped) {
      continue;
    }
    const translation = node.translation ? node.translation : {};
    const label =
      language === "en"
        ? translation.en
          ? translation.en
          : ""
        : translation.it
        ? translation.it
        : "";
    if (normalizeLabel(label) !== normalized) {
      continue;
    }
    if (!node.deleted) {
      return { label, deleted: false };
    }
    if (!hidden) {
      hidden = { label, deleted: true };
    }
  }

  return hidden;
}

// END - Duplicated labels


// BEGIN - Data types catalogue

/**
 * Text to show for a data type: its back-end label, falling back on the technical name
 * when the row carries none. The fallback is not cosmetic: an unlabelled row would
 * otherwise become an invisible option in the picker.
 *
 * @since 3.0.0
 */
function readDataTypeLabel(dataType: DataTypeSummary): string {
  if (dataType.dataTypeLabel) {
    return dataType.dataTypeLabel;
  }
  return dataType.dataTypeName ? dataType.dataTypeName : "";
}

/**
 * Label of every data type of the catalogue, indexed by id.
 *
 * The text comes from the back-end (`dataTypeLabel`): the catalogue owns its own
 * labels, the front-end keeps no dictionary of its own to drift from them.
 *
 * Two data types could carry the same label, and the user would then face two
 * identical options for two different forms: whenever that happens the technical name
 * is appended, so an option in a picker is never ambiguous.
 *
 * @since 3.0.0
 *
 * @param dataTypes Catalogue as returned by GET /data-types
 */
export function buildDataTypeLabels(dataTypes: DataTypeSummary[]): {
  [dataTypeId: string]: string;
} {
  const labels: { [dataTypeId: string]: string } = {};
  const catalog = Array.isArray(dataTypes) ? dataTypes : [];
  const usage: { [label: string]: number } = {};

  catalog.forEach((dataType) => {
    if (!dataType || !dataType._id) {
      return;
    }
    const label = readDataTypeLabel(dataType);
    if (label) {
      usage[label] = (usage[label] ? usage[label] : 0) + 1;
    }
  });

  catalog.forEach((dataType) => {
    if (!dataType || !dataType._id) {
      return;
    }
    const id = dataType._id.toString();
    const name = dataType.dataTypeName ? dataType.dataTypeName : "";
    const label = readDataTypeLabel(dataType);
    if (!label) {
      // Nothing readable to show: the id is at least an exact reference, and an option
      // with an empty label would look like a broken blank row
      labels[id] = id;
      return;
    }
    labels[id] =
      usage[label] > 1 && name && name !== label ? `${label} (${name})` : label;
  });

  return labels;
}

// END - Data types catalogue

// BEGIN - misc

/**
 * Suggest the next free area code (A, B, ... Z, then A1, B1, ...) given the codes
 * already in use. Purely a convenience for the creation form: the user can overwrite it.
 *
 * @since 3.0.0
 */
export function suggestAreaCode(usedCodes: string[]): string {
  const used: { [code: string]: boolean } = {};
  (usedCodes || []).forEach((code) => {
    if (code) {
      used[code.toUpperCase()] = true;
    }
  });
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  for (let suffix = 0; suffix <= 9; suffix++) {
    for (let index = 0; index < alphabet.length; index++) {
      const candidate =
        suffix === 0 ? alphabet.charAt(index) : alphabet.charAt(index) + suffix;
      if (!used[candidate]) {
        return candidate;
      }
    }
  }
  return "";
}

// END - misc
